/**
 * BlockNote Editor Controller
 *
 * Universal Stimulus controller for BlockNote rich text editor initialization
 * and content management. This controller is resource-agnostic and can be used
 * with any model that stores JSONB content (Documents, Pages, etc.).
 *
 * Features:
 * - BlockNote editor initialization with React
 * - Content change detection and propagation
 * - File upload support (images, videos, audio, files)
 * - Save status indicators
 * - Resource type scoping
 * - Configurable API endpoints for images and links
 *
 * Usage:
 *   <div data-controller="blocks-text-editor blocks-text-autosave"
 *        data-blocks-text-editor-content-value="<%= @resource.content.to_json %>"
 *        data-blocks-text-editor-resource-type-value="Document"
 *        data-blocks-text-editor-image-upload-url-value="/api/v1/blocknote/images/upload"
 *        data-blocks-text-editor-link-preview-url-value="/api/v1/blocknote/links/preview"
 *        data-blocks-text-autosave-autosave-url-value="<%= autosave_document_path(@document) %>"
 *        data-action="blocks-text-editor:contentChanged->blocks-text-autosave#contentChanged">
 *     <div data-blocks-text-editor-target="editor"></div>
 *     <input type="hidden" data-blocks-text-editor-target="content" name="document[content]">
 *     <span data-blocks-text-editor-target="saveIndicator"></span>
 *   </div>
 *
 * Targets:
 *   editor         - Container div where BlockNote editor will be mounted
 *   content        - Hidden form field that stores JSON content
 *   saveIndicator  - Optional span for save status messages
 *
 * Values:
 *   content           - Initial content as JSON string (array of blocks)
 *   resourceType      - Resource type for scoping (e.g., "Document", "Page")
 *   fileUploadUrl    - API endpoint for file uploads (enables upload for images, videos, audio, documents)
 *   linkPreviewUrl    - API endpoint for link metadata previews
 *
 * Events Dispatched:
 *   contentChanged    - Fired when editor content changes
 *                       { detail: { content: jsonString } }
 *
 * File Upload:
 *   When fileUploadUrl is provided, BlockNote automatically shows an "Upload" tab
 *   in the image/video/audio/file block toolbars. Files are uploaded via FormData
 *   with CSRF protection to the specified endpoint.
 *
 * Related Files:
 * - app/components/ui/blocks_text_editor_component.rb
 * - app/javascript/controllers/blocks_text_autosave_controller.js
 * - app/controllers/api/v1/blocknote/images_controller.rb
 * - app/controllers/api/v1/blocknote/links_controller.rb
 *
 * Dependencies:
 * - @hotwired/stimulus
 * - @blocknote/core
 * - @blocknote/mantine
 * - @blocknote/react
 * - react
 * - react-dom/client
 */

import { Controller } from "@hotwired/stimulus";
import {
  BlockNoteSchema,
  defaultBlockSpecs,
  defaultProps,
  filterSuggestionItems,
} from "@blocknote/core";
import { BlockNoteView } from "@blocknote/mantine";
import {
  useCreateBlockNote,
  createReactBlockSpec,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
} from "@blocknote/react";
import "@blocknote/mantine/style.css";

// Import React for rendering
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

// Import custom components
import TableOfContentsBlock from "../components/blocknote/TableOfContentsBlock.jsx";
import NoteBlock from "../components/blocknote/NoteBlock.jsx";
import DiscountCardBlock from "../components/blocknote/DiscountCardBlock.jsx";
import ProductCardSimpleBlock from "../components/blocknote/ProductCardSimpleBlock.jsx";
import ProductCardExpandedBlock from "../components/blocknote/ProductCardExpandedBlock.jsx";
// Marketing blocks
import HeroSectionBlock from "../components/blocknote/HeroSectionBlock.jsx";
import FeatureSectionBlock from "../components/blocknote/FeatureSectionBlock.jsx";
import CTASectionBlock from "../components/blocknote/CTASectionBlock.jsx";
import NewsletterSignupBlock from "../components/blocknote/NewsletterSignupBlock.jsx";
// Advanced image block with link, alt text, and media browser support
import AdvancedImageBlock from "../components/blocknote/AdvancedImageBlock.jsx";

/**
 * Create Table of Contents block spec
 * createReactBlockSpec returns a FUNCTION that must be called to get the BlockSpec
 */
const createTableOfContents = createReactBlockSpec(
  {
    type: "tableOfContents",
    propSchema: {
      includedLevels: {
        default: [2, 3],
      },
      hiddenHeadings: {
        default: [],
      },
    },
    content: "none",
  },
  {
    render: (props) => <TableOfContentsBlock {...props} />,
  },
);

/**
 * Create Note block spec
 */
const createNoteBlock = createReactBlockSpec(
  {
    type: "noteBlock",
    propSchema: {
      title: {
        default: "",
      },
      body: {
        default: "",
      },
      noteType: {
        default: "info",
      },
    },
    content: "none",
  },
  {
    render: (props) => <NoteBlock {...props} />,
  },
);

/**
 * Create Discount Card block spec
 */
const createDiscountCardBlock = createReactBlockSpec(
  {
    type: "discountCard",
    propSchema: {
      logoImageUrl: {
        default: "",
      },
      title: {
        default: "",
      },
      description: {
        default: "",
      },
      details: {
        default: "",
      },
      linkUrl: {
        default: "",
      },
      linkText: {
        default: "View Deal",
      },
      badgeText: {
        default: "",
      },
      badgeColor: {
        default: "green",
      },
    },
    content: "none",
  },
  {
    render: (props) => <DiscountCardBlock {...props} />,
  },
);

/**
 * Create Product Card Simple block spec
 */
const createProductCardSimpleBlock = createReactBlockSpec(
  {
    type: "productCardSimple",
    propSchema: {
      productImage: {
        default: "",
      },
      title: {
        default: "",
      },
      msrp: {
        default: "",
      },
      details: {
        default: "",
      },
      badge: {
        default: "",
      },
      imageFit: {
        // "cover" (edge-to-edge, default) or "contain" (padded)
        default: "cover",
      },
      horizontalLayout: {
        // true for 2-column layout (image left, text right) in SSR
        default: false,
      },
      links: {
        default: [],
      },
    },
    content: "none",
  },
  {
    render: (props) => <ProductCardSimpleBlock {...props} />,
  },
);

/**
 * Create Product Card Expanded block spec
 */
const createProductCardExpandedBlock = createReactBlockSpec(
  {
    type: "productCardExpanded",
    propSchema: {
      title: {
        default: "",
      },
      description: {
        default: "",
      },
      productImageUrl: {
        default: "",
      },
      linkUrl: {
        default: "",
      },
      linkText: {
        default: "",
      },
      badge: {
        default: "",
      },
      features: {
        default: [
          { title: "", value: "" },
          { title: "", value: "" },
          { title: "", value: "" },
          { title: "", value: "" },
          { title: "", value: "" },
        ],
      },
      review: {
        default: "",
      },
      prosLabel: {
        default: "",
      },
      consLabel: {
        default: "",
      },
      pros: {
        default: [],
      },
      cons: {
        default: [],
      },
    },
    content: "none",
  },
  {
    render: (props) => <ProductCardExpandedBlock {...props} />,
  },
);

/**
 * Create Hero Section block spec (Marketing)
 */
const createHeroSectionBlock = createReactBlockSpec(
  {
    type: "heroSection",
    propSchema: {
      smallTitle: { default: "" },
      title: { default: "" },
      description: { default: "" },
      detail: { default: "" },
      buttons: { default: [] },
      featuredMedia: { default: null },
      backgroundMedia: { default: null },
      backgroundColor: { default: "" },
      layout: { default: "text-left" },
      contentAlign: { default: "left" },
    },
    content: "none",
  },
  {
    render: (props) => <HeroSectionBlock {...props} />,
  },
);
/**
 * Create Newsletter Signup block spec
 */
const createNewsletterSignupBlock = createReactBlockSpec(
  {
    type: "newsletterSignup",
    propSchema: {
      layout: {
        default: "centered",
      },
      title: {
        default: "Stay Updated",
      },
      description: {
        default: "Get our latest news delivered to your inbox",
      },
      // Multi-list support
      newsletterListIds: {
        default: [],
      },
      // Opt-in strategy configuration - required field for GDPR compliance
      optInStrategy: {
        default: "",
      },
      // Welcome email configuration - only applies to double opt-in
      sendWelcomeEmail: {
        default: false,
      },
      // Single name field - simpler alternative to first/last name
      showName: {
        default: false,
      },
      namePlaceholder: {
        default: "Your name",
      },
      showFirstName: {
        default: false,
      },
      showLastName: {
        default: false,
      },
      firstNamePlaceholder: {
        default: "First name",
      },
      lastNamePlaceholder: {
        default: "Last name",
      },
      emailPlaceholder: {
        default: "Enter your email address",
      },
      ctaButtonText: {
        default: "Subscribe",
      },
      successMessage: {
        default: "Thanks for subscribing! Check your email to confirm.",
      },
      thankYouPage: {
        default: "",
      },
      privacyNotice: {
        default: "We respect your privacy. Unsubscribe at any time.",
      },
      privacyLink: {
        default: "/privacy",
      },
      media: {
        default: "",
      },
      overlayOpacity: {
        default: 70,
      },
    },
    content: "none",
  },
  {
    render: (props) => <NewsletterSignupBlock {...props} />,
  },
);

/**
 * Create Feature Section block spec (Marketing)
 */
const createFeatureSectionBlock = createReactBlockSpec(
  {
    type: "featureSection",
    propSchema: {
      smallTitle: { default: "" },
      title: { default: "" },
      description: { default: "" },
      features: { default: [] },
      featuredMedia: { default: null },
      backgroundMedia: { default: null },
      layout: { default: "image-right" },
    },
    content: "none",
  },
  {
    render: (props) => <FeatureSectionBlock {...props} />,
  },
);

/**
 * Create CTA Section block spec (Marketing)
 */
const createCTASectionBlock = createReactBlockSpec(
  {
    type: "ctaSection",
    propSchema: {
      smallTitle: { default: "" },
      title: { default: "" },
      description: { default: "" },
      buttons: { default: [] },
      featuredMedia: { default: null },
      backgroundMedia: { default: null },
      layout: { default: "split-left" },
    },
    content: "none",
  },
  {
    render: (props) => <CTASectionBlock {...props} />,
  },
);

/**
 * Create Advanced Image block spec
 *
 * Custom image block with enhanced features:
 * - Link assignment (make images clickable)
 * - Media browser integration (select from existing library)
 * - Alt text support with filename fallback (SEO optimization)
 */
const createAdvancedImageBlock = createReactBlockSpec(
  {
    type: "advancedImage",
    propSchema: {
      // Core image properties
      url: { default: "" },
      caption: { default: "" },
      alt: { default: "" },
      // Link properties
      linkUrl: { default: "" },
      linkTarget: { default: "_blank" },
      linkNofollow: { default: false },
      // ActiveStorage blob ID for SSR variant generation
      blobSignedId: { default: "" },
    },
    content: "none",
  },
  {
    render: (props) => <AdvancedImageBlock {...props} />,
  },
);

/**
 * Create custom schema with extended blocks
 *
 * This extends the default BlockNote schema to add:
 * - An 'id' property to heading blocks for TOC navigation
 * - Custom blocks: tableOfContents, noteBlock, discountCard, productCardSimple, productCardExpanded, newsletterSignup
 * - Marketing blocks: heroSection, featureSection, ctaSection
 * - Advanced image block with link, alt text, and media browser support
 *
 * Note: The default 'image' block is kept as-is for backward compatibility.
 * Use 'advancedImage' for enhanced image features (links, alt text, media browser).
 */
const schema = BlockNoteSchema.create({
  blockSpecs: {
    // Spread all default blocks (including image, video, audio, file)
    ...defaultBlockSpecs,
    // Extend heading block with ID property for TOC navigation
    heading: {
      ...defaultBlockSpecs.heading,
      propSchema: {
        ...defaultBlockSpecs.heading.propSchema,
        id: {
          default: undefined,
        },
      },
    },
    // Add custom blocks - CALL the function returned by createReactBlockSpec
    tableOfContents: createTableOfContents(),
    noteBlock: createNoteBlock(),
    discountCard: createDiscountCardBlock(),
    productCardSimple: createProductCardSimpleBlock(),
    productCardExpanded: createProductCardExpandedBlock(),
    // Marketing blocks (Pages only)
    heroSection: createHeroSectionBlock(),
    featureSection: createFeatureSectionBlock(),
    ctaSection: createCTASectionBlock(),
    newsletterSignup: createNewsletterSignupBlock(),
    // Advanced image block with link, alt text, and media browser support
    advancedImage: createAdvancedImageBlock(),
  },
});

/**
 * Custom slash menu item for Table of Contents
 */
const insertTableOfContents = (editor) => ({
  title: "Table of Contents",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "tableOfContents",
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["toc", "contents", "navigation", "outline", "index"],
  group: "Other",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M3 4h18v2H3V4zm0 7h12v2H3v-2zm0 7h18v2H3v-2z" />
    </svg>
  ),
  subtext: "Insert a table of contents from document headings",
});

/**
 * Custom slash menu item for Note Block
 */
const insertNoteBlock = (editor) => ({
  title: "Note",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "noteBlock",
          props: {
            title: "Note",
            body: "",
            noteType: "info",
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["note", "aside", "callout", "alert", "info"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  subtext: "Insert an informational note or callout",
});

/**
 * Custom slash menu item for Discount Card (Pages only)
 */
const insertDiscountCard = (editor) => ({
  title: "Discount Card",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "discountCard",
          props: {
            title: "",
            description: "",
            details: "",
            linkUrl: "",
            linkText: "View Deal",
            badgeText: "",
            badgeColor: "green",
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["discount", "deal", "offer", "promo"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z"
        clipRule="evenodd"
      />
    </svg>
  ),
  subtext: "Insert a discount or promotional card",
});

/**
 * Custom slash menu item for Product Card Simple (Pages only)
 */
const insertProductCardSimple = (editor) => ({
  title: "Product Card (Simple)",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "productCardSimple",
          props: {
            productImage: "",
            title: "",
            msrp: "",
            details: "",
            badge: "",
            links: [],
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["product", "recommendation", "review"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 004.25 22.5h15.5a1.875 1.875 0 001.865-2.071l-1.263-12a1.875 1.875 0 00-1.865-1.679H16.5V6a4.5 4.5 0 10-9 0zM12 3a3 3 0 00-3 3v.75h6V6a3 3 0 00-3-3zm-3 8.25a3 3 0 106 0v-.75a.75.75 0 011.5 0v.75a4.5 4.5 0 11-9 0v-.75a.75.75 0 011.5 0v.75z" />
    </svg>
  ),
  subtext: "Insert a simple product showcase card",
});

/**
 * Custom slash menu item for Product Card Expanded (Pages only)
 */
const insertProductCardExpanded = (editor) => ({
  title: "Product Card (Expanded)",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "productCardExpanded",
          props: {
            title: "",
            description: "",
            productImageUrl: "",
            linkUrl: "",
            linkText: "View Product",
            badge: "",
            features: [
              { title: "", value: "" },
              { title: "", value: "" },
              { title: "", value: "" },
              { title: "", value: "" },
              { title: "", value: "" },
            ],
            pros: [],
            cons: [],
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["comparison", "detailed", "specs"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 004.25 22.5h15.5a1.875 1.875 0 001.865-2.071l-1.263-12a1.875 1.875 0 00-1.865-1.679H16.5V6a4.5 4.5 0 10-9 0zM12 3a3 3 0 00-3 3v.75h6V6a3 3 0 00-3-3zm-3 8.25a3 3 0 106 0v-.75a.75.75 0 011.5 0v.75a4.5 4.5 0 11-9 0v-.75a.75.75 0 011.5 0v.75z"
        clipRule="evenodd"
      />
    </svg>
  ),
  subtext: "Insert a detailed product comparison card",
});

/**
 * Custom slash menu item for Hero Section (Marketing)
 */
const insertHeroSection = (editor) => ({
  title: "Hero Section",
  onItemClick: () => {
    editor.insertBlocks(
      [{ type: "heroSection" }],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["hero", "banner", "header"],
  group: "Marketing Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  ),
  subtext: "Insert a hero section for landing pages",
});
/**
 * Custom slash menu item for Newsletter Signup (Pages only)
 */
const insertNewsletterSignup = (editor) => ({
  title: "Newsletter Sign-up",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "newsletterSignup",
          props: {
            layout: "centered",
            title: "Stay Updated",
            description: "Get our latest news delivered to your inbox",
            newsletterListIds: [],
            showFirstName: false,
            showLastName: false,
            firstNamePlaceholder: "First name",
            lastNamePlaceholder: "Last name",
            emailPlaceholder: "Enter your email address",
            ctaButtonText: "Subscribe",
            successMessage:
              "Thanks for subscribing! Check your email to confirm.",
            thankYouPage: "",
            privacyNotice: "We respect your privacy. Unsubscribe at any time.",
            privacyLink: "/privacy",
            media: "",
            overlayOpacity: 70,
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["newsletter", "signup", "subscribe", "email", "subscribe form"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
    </svg>
  ),
  subtext: "Insert a newsletter sign-up form",
});

/**
 * Custom slash menu item for Feature Section (Marketing)
 */
const insertFeatureSection = (editor) => ({
  title: "Feature Section",
  onItemClick: () => {
    editor.insertBlocks(
      [{ type: "featureSection" }],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["features", "benefits"],
  group: "Marketing Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  subtext: "Insert a features section with icons",
});

/**
 * Custom slash menu item for CTA Section (Marketing)
 */
const insertCTASection = (editor) => ({
  title: "CTA Section",
  onItemClick: () => {
    editor.insertBlocks(
      [{ type: "ctaSection" }],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["cta", "call to action", "conversion"],
  group: "Marketing Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  subtext: "Insert a call-to-action section",
});

/**
 * Custom slash menu item for Advanced Image (Pages only)
 *
 * Enhanced image block with link assignment, alt text, and media browser integration
 */
const insertAdvancedImage = (editor) => ({
  title: "Advanced Image",
  onItemClick: () => {
    editor.insertBlocks(
      [
        {
          type: "advancedImage",
          props: {
            url: "",
            caption: "",
            alt: "",
            linkUrl: "",
            linkTarget: "_blank",
            linkNofollow: false,
            blobSignedId: "",
          },
        },
      ],
      editor.getTextCursorPosition().block,
      "after",
    );
  },
  aliases: ["image", "picture", "photo", "linked image", "seo image"],
  group: "Content Blocks",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path
        fillRule="evenodd"
        d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
        clipRule="evenodd"
      />
    </svg>
  ),
  subtext: "Image with links, alt text & media browser",
});

/**
 * Get custom slash menu items with resource type filtering
 *
 * Documents get only default BlockNote blocks (paragraphs, headings, lists, etc.)
 * Pages get additional custom blocks (TOC, notes, marketing blocks, etc.)
 */
const getCustomSlashMenuItems = (editor, resourceType) => {
  const baseItems = [
    ...getDefaultReactSlashMenuItems(editor),
  ];

  // Add page-only blocks when resource type is Page
  if (resourceType === "Page") {
    baseItems.push(
      // Content organization blocks
      insertTableOfContents(editor),
      insertNoteBlock(editor),
      // Advanced image with links, alt text, media browser
      insertAdvancedImage(editor),
      // Content cards
      insertDiscountCard(editor),
      insertProductCardSimple(editor),
      insertProductCardExpanded(editor),
      // Marketing blocks
      insertHeroSection(editor),
      insertFeatureSection(editor),
      insertCTASection(editor),
      insertNewsletterSignup(editor),
    );
  }

  return baseItems;
};

/**
 * React component wrapper for BlockNote editor
 *
 * @param {Object} props
 * @param {Array} props.initialContent - Array of BlockNote blocks
 * @param {Function} props.onChange - Callback when content changes
 * @param {String} props.resourceType - Resource type for scoping
 * @param {String} props.fileUploadUrl - API endpoint for file uploads (images, videos, audio, documents)
 */
function Editor({ initialContent, onChange, resourceType, fileUploadUrl }) {
  // Define the uploadFile function for BlockNote
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("resource_type", resourceType);

    try {
      const response = await fetch(fileUploadUrl, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload failed");
      }

      const data = await response.json();
      if (data.success && data.url) {
        return data.url;
      } else {
        throw new Error(data.error || "Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  // Debug logging removed

  const editor = useCreateBlockNote({
    schema,
    initialContent: initialContent,
    uploadFile: fileUploadUrl ? uploadFile : undefined,
    // domAttributes accepts only static attributes. We assign DOM ids post-render below.
  });

  // Generate IDs for headings that don't have them
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const document = editor.document;
        const existingIds = new Set();
        let needsUpdate = false;

        // First pass: collect existing IDs
        document.forEach((block) => {
          if (block.type === "heading" && block.props?.id) {
            existingIds.add(block.props.id);
          }
        });

        // Second pass: generate IDs for headings without them
        const updatedDocument = document.map((block) => {
          if (
            block.type === "heading" &&
            (!block.props?.id || block.props.id === "")
          ) {
            needsUpdate = true;
            const text = block.content?.[0]?.text || "";
            const baseId =
              text
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "") || "heading";

            let id = baseId;
            let counter = 2;
            while (existingIds.has(id)) {
              id = `${baseId}-${counter}`;
              counter++;
            }
            existingIds.add(id);

            return {
              ...block,
              props: {
                ...block.props,
                id: id,
              },
            };
          }
          return block;
        });

        if (needsUpdate) {
          editor.replaceBlocks(document, updatedDocument);
        }
      } catch (error) {}
    }, 100);

    return () => clearTimeout(timer);
  }, [editor]);

  // After render (and on changes), ensure heading DOM elements have id attributes
  // matching their block.props.id (slug), so TOC jump links target correctly.
  useEffect(() => {
    const applyHeadingDomIds = () => {
      try {
        // Determine a sensible root to limit queries
        const root =
          editor?._tiptapEditor?.options?.element?.parentElement || document;

        // Build map of block.id -> props.id (slug) for heading blocks
        const idMap = new Map();
        if (editor && Array.isArray(editor.document)) {
          editor.document.forEach((block) => {
            if (block && block.type === "heading" && block.props?.id) {
              idMap.set(block.id, block.props.id);
            }
          });
        }

        // Walk rendered blocks and apply DOM id to both wrapper and h* node
        const blockEls = root.querySelectorAll(
          '.bn-block[data-node-type="blockContainer"]',
        );
        blockEls.forEach((blockEl) => {
          const bnBlockId = blockEl.getAttribute("data-id");
          const headingSlug = bnBlockId ? idMap.get(bnBlockId) : undefined;
          if (!headingSlug) return;

          const contentEl = blockEl.querySelector(
            '.bn-block-content[data-content-type="heading"]',
          );
          if (contentEl && contentEl.id !== headingSlug) {
            contentEl.id = headingSlug;
          }
          const headingEl = contentEl?.querySelector("h1,h2,h3,h4,h5,h6");
          if (headingEl && headingEl.id !== headingSlug) {
            headingEl.id = headingSlug;
          }
        });
      } catch (e) {}
    };

    // Initial apply
    applyHeadingDomIds();

    // Keep in sync during edits
    const unsubscribe = editor.onChange(() => applyHeadingDomIds());
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [editor]);

  useEffect(() => {
    if (onChange) {
      return editor.onChange(() => {
        onChange(editor.document);
      });
    }
  }, [editor, onChange]);

  return (
    <BlockNoteView editor={editor} theme="light" slashMenu={false}>
      <SuggestionMenuController
        triggerCharacter={"/"}
        getItems={async (query) =>
          filterSuggestionItems(
            getCustomSlashMenuItems(editor, resourceType),
            query,
          )
        }
      />
    </BlockNoteView>
  );
}

export default class extends Controller {
  static targets = ["editor", "content", "saveIndicator"];
  static values = {
    content: String,
    resourceType: { type: String, default: "Resource" },
    fileUploadUrl: String,
    linkPreviewUrl: String,
  };

  /**
   * Lifecycle: Called when controller connects to the DOM
   * Initializes the BlockNote editor
   */
  connect() {
    this.initializeEditor();
    this.setupContainerClickHandler();
  }

  /**
   * Sets up click handler on the editor container to focus the editor
   * This improves UX by allowing users to click anywhere in the container
   * to start typing, not just on the actual editable area
   */
  setupContainerClickHandler() {
    this.boundContainerClickHandler = this.handleContainerClick.bind(this);
    this.editorTarget.addEventListener("click", this.boundContainerClickHandler);
  }

  /**
   * Handles clicks on the editor container
   * Focuses the contenteditable element if click was on the container itself
   * (not on an existing block or interactive element)
   *
   * @param {Event} event - Click event
   */
  handleContainerClick(event) {
    // Find the contenteditable element inside BlockNote
    const contentEditable = this.editorTarget.querySelector("[contenteditable='true']");
    if (!contentEditable) return;

    // Check if the click was directly on the editor container or its immediate wrapper
    // but not on actual content blocks
    const clickTarget = event.target;
    const isOnContainer = clickTarget === this.editorTarget ||
                          clickTarget.classList.contains("bn-container") ||
                          clickTarget.classList.contains("bn-editor");

    // Also check if click is on empty space below content
    const blockGroup = this.editorTarget.querySelector(".bn-block-group");
    const isOnEmptySpace = blockGroup && !blockGroup.contains(clickTarget) &&
                           this.editorTarget.contains(clickTarget);

    if (isOnContainer || isOnEmptySpace) {
      // Focus the editor
      contentEditable.focus();

      // Move cursor to the end of the last block
      this.moveCursorToEnd(contentEditable);
    }
  }

  /**
   * Moves the cursor to the end of the contenteditable element
   *
   * @param {Element} element - The contenteditable element
   */
  moveCursorToEnd(element) {
    const selection = window.getSelection();
    const range = document.createRange();

    // Find the last text node or element to position cursor
    const lastBlock = element.querySelector(".bn-block-group > .bn-block-outer:last-child");
    if (lastBlock) {
      const lastContent = lastBlock.querySelector(".bn-inline-content");
      if (lastContent) {
        range.selectNodeContents(lastContent);
        range.collapse(false); // Collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  /**
   * Lifecycle: Called when controller disconnects from the DOM
   * Cleans up React root and event listeners to prevent memory leaks
   */
  disconnect() {
    // Remove click handler
    if (this.boundContainerClickHandler) {
      this.editorTarget.removeEventListener("click", this.boundContainerClickHandler);
    }

    // Clear any pending save indicator timeout
    if (this.saveIndicatorHideTimeout) {
      clearTimeout(this.saveIndicatorHideTimeout);
    }

    // Unmount React root
    if (this.root) {
      this.root.unmount();
    }
  }

  /**
   * Initializes the BlockNote editor with React
   *
   * Parses initial content from various formats (array, string, empty)
   * and mounts the BlockNote React component. Wires up change handler
   * to update hidden form field and trigger autosave.
   *
   * @async
   */
  async initializeEditor() {
    try {
      // Parse initial content - BlockNote expects an array
      let initialContent;

      if (
        this.hasContentValue &&
        this.contentValue &&
        this.contentValue.trim() !== ""
      ) {
        try {
          const parsed = JSON.parse(this.contentValue);

          // Ensure we have an array
          if (Array.isArray(parsed)) {
            // If array is empty or has content, use it
            initialContent = parsed.length > 0 ? parsed : undefined;
          } else {
            // Not an array, use undefined to let BlockNote use default
            initialContent = undefined;
          }
        } catch (e) {
          initialContent = undefined;
        }
      } else {
        initialContent = undefined;
      }

      // Create React root and render the editor
      this.root = createRoot(this.editorTarget);
      this.root.render(
        React.createElement(Editor, {
          initialContent: initialContent,
          resourceType: this.resourceTypeValue,
          fileUploadUrl: this.fileUploadUrlValue,
          onChange: (document) => {
            this.handleContentChange(document);
          },
        }),
      );
    } catch (error) {
      console.error("Error initializing BlockNote editor:", error);
    }
  }

  /**
   * Handles content changes from the BlockNote editor
   *
   * Updates the hidden form field with JSON content and dispatches
   * a custom event to trigger autosave functionality.
   *
   * @param {Array} document - BlockNote document (array of blocks)
   */
  handleContentChange(document) {
    try {
      // Convert document to JSON string
      const contentJson = JSON.stringify(document);

      // Update hidden form field
      if (this.hasContentTarget) {
        this.contentTarget.value = contentJson;
      }

      // Trigger autosave by dispatching event
      this.dispatch("contentChanged", { detail: { content: contentJson } });
    } catch (error) {
      console.error("Error handling content change:", error);
    }
  }

  /**
   * Shows a save status indicator message
   *
   * @param {String} message - Message to display
   * @param {String} type - Message type: "saving", "success", or "error"
   */
  showSaveIndicator(message, type = "success") {
    if (!this.hasSaveIndicatorTarget) return;

    // Clear any pending auto-hide timeout to prevent race conditions
    if (this.saveIndicatorHideTimeout) {
      clearTimeout(this.saveIndicatorHideTimeout);
      this.saveIndicatorHideTimeout = null;
    }

    const indicator = this.saveIndicatorTarget;
    indicator.textContent = message || "\u00A0";

    // Remove state classes
    indicator.classList.remove("badge-success", "badge-error", "opacity-0", "opacity-100");

    // Add appropriate classes
    if (type === "saving") {
      indicator.classList.add("opacity-100");
    } else if (type === "success") {
      indicator.classList.add("badge-success", "opacity-100");
    } else if (type === "error") {
      indicator.classList.add("badge-error", "opacity-100");
    }

    // Auto-hide after 2 seconds for success/error
    if (type === "success" || type === "error") {
      this.saveIndicatorHideTimeout = setTimeout(() => {
        indicator.textContent = "\u00A0";
        indicator.classList.remove("badge-success", "badge-error", "opacity-100");
        indicator.classList.add("opacity-0");
      }, 2000);
    }
  }
}
